import { ScopeDIProvider, requireService, wrap } from '@repo/di-ez';
import {
  DoneRoundLight as CheckMarkIcon,
  CloseRoundLight as CrossIcon,
  RegroupLight as SwapIcon,
} from '@repo/go-icons';
import { Button, DatePickerV2, Field, Scrollbar, Select } from '@repo/go-ui';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FiltersUiStore } from './filters-ui.store';
import styles from './filters.module.scss';
import { FiltersI18nProvider } from './i18n/FiltersI18nProvider';
import type { FiltersService } from './service/filters-service';
import type { FiltersWidgetProps } from './types/external-services';

const FiltersWidgetContent = wrap(function FiltersWidgetContent() {
  const { t } = useTranslation();
  const filtersService = requireService('filtersService');

  useEffect(() => {
    void filtersService.loadDirectories();
  }, [filtersService]);

  const { draft } = filtersService;

  return (
    <>
      <div className={styles.fieldsScroll}>
        <Scrollbar>
          <div className={styles.fields}>
            <Field label={t('Labels:status.label')} className={styles.field}>
              <Select
                mode="multiple"
                allowClear
                loading={filtersService.isLoading}
                value={draft.status}
                options={filtersService.getStatusOptions(t)}
                placeholder={t('Labels:status.placeholder')}
                onChange={(value) => filtersService.setStatus((value as Array<string | number>) ?? [])}
              />
            </Field>

            <Field label={t('Labels:dateRange.label')} className={styles.field}>
              <div className={styles.dateRange}>
                <DatePickerV2
                  allowClear
                  className={styles.dateInput}
                  value={draft.workStartDateFrom}
                  placeholder={t('common:DatePicker.defaultPlaceholder')}
                  onChange={filtersService.setWorkStartDateFrom}
                />
                <span className={styles.dateDash}>—</span>
                <DatePickerV2
                  allowClear
                  className={styles.dateInput}
                  value={draft.workStartDateTo}
                  placeholder={t('common:DatePicker.defaultPlaceholder')}
                  onChange={filtersService.setWorkStartDateTo}
                />
              </div>
            </Field>

            <Field label={t('Labels:organization.label')} className={styles.field}>
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                loading={filtersService.isLoading}
                value={draft.organizationId}
                options={filtersService.getOrganizationOptions()}
                placeholder={t('Labels:organization.placeholder')}
                onChange={(value) => filtersService.setOrganizationId((value as Array<string | number>) ?? [])}
              />
            </Field>

            <Field label={t('Labels:block.label')} className={styles.field}>
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                loading={filtersService.isLoading}
                value={draft.blockId}
                options={filtersService.getBlockOptions()}
                placeholder={t('Labels:block.placeholder')}
                onChange={(value) => filtersService.setBlockId((value as Array<string | number>) ?? [])}
              />
            </Field>

            <Field label={t('Labels:field.label')} className={styles.field}>
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                loading={filtersService.isLoading}
                value={draft.fieldId}
                options={filtersService.getFieldOptions()}
                placeholder={t('Labels:field.placeholder')}
                onChange={(value) => filtersService.setFieldId((value as Array<string | number>) ?? [])}
              />
            </Field>

            <Field label={t('Labels:pad.label')} className={styles.field}>
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                loading={filtersService.isLoading}
                value={draft.padId}
                options={filtersService.getPadOptions()}
                placeholder={t('Labels:pad.placeholder')}
                onChange={(value) => filtersService.setPadId((value as Array<string | number>) ?? [])}
              />
            </Field>

            <Field label={t('Labels:well.label')} className={styles.field}>
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                loading={filtersService.isLoading}
                value={draft.wellId}
                options={filtersService.getWellOptions()}
                placeholder={t('Labels:well.placeholder')}
                onChange={(value) => filtersService.setWellId((value as Array<string | number>) ?? [])}
              />
            </Field>

            <Field label={t('Labels:rigModel.label')} className={styles.field}>
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                loading={filtersService.isLoading}
                value={draft.rigModelId}
                options={filtersService.getRigModelOptions()}
                placeholder={t('Labels:rigModel.placeholder')}
                onChange={(value) => filtersService.setRigModelId((value as Array<string | number>) ?? [])}
              />
            </Field>

            <Field label={t('Labels:brigade.label')} className={styles.field}>
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                loading={filtersService.isLoading}
                value={draft.brigadeId}
                options={filtersService.getBrigadeOptions()}
                placeholder={t('Labels:brigade.placeholder')}
                onChange={(value) => filtersService.setBrigadeId((value as Array<string | number>) ?? [])}
              />
            </Field>

            <Field label={t('Labels:wellboreType.label')} className={styles.field}>
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                loading={filtersService.isLoading}
                value={draft.wellboreTypeInstanceId}
                options={filtersService.getWellboreTypeInstanceOptions()}
                placeholder={t('Labels:wellboreType.placeholder')}
                onChange={(value) => filtersService.setWellboreTypeInstanceId((value as Array<string | number>) ?? [])}
              />
            </Field>
          </div>
        </Scrollbar>
      </div>

      <div className={styles.buttons}>
        <div className={styles.rowButtons}>
          <Button
            variant="danger"
            className={styles.button}
            icon={<CrossIcon />}
            disabled={filtersService.isCancelDisabled}
            onClick={filtersService.cancel}
          >
            {t('common:Buttons.cancel')}
          </Button>
          <Button
            variant="success"
            className={styles.button}
            icon={<CheckMarkIcon />}
            disabled={filtersService.isApplyDisabled}
            onClick={filtersService.apply}
          >
            {t('common:Buttons.accept')}
          </Button>
        </div>

        <Button
          variant="primary"
          className={styles.button}
          icon={<SwapIcon />}
          disabled={filtersService.isResetDisabled}
          onClick={filtersService.reset}
        >
          {t('wellsFilter:Buttons.reset')}
        </Button>
      </div>
    </>
  );
});

export const FiltersWidget = wrap(function FiltersWidget({
  filtersService,
  language,
  parentI18n,
  initialState,
  onStateChange,
}: FiltersWidgetProps) {
  const [uiStore] = useState(() => new FiltersUiStore(initialState));

  useEffect(() => {
    filtersService.hydrate(initialState);
  }, [filtersService, initialState]);

  useEffect(() => {
    uiStore.setExternalCallbacks({ onStateChange });
    filtersService.setOnAppliedChange(onStateChange);

    return () => {
      filtersService.setOnAppliedChange(undefined);
    };
  }, [filtersService, uiStore, onStateChange]);

  const init = useCallback(() => {
    return {
      filtersService,
      filtersUiStore: uiStore,
    };
  }, [filtersService, uiStore]);

  return (
    <FiltersI18nProvider language={language} parentI18n={parentI18n}>
      <ScopeDIProvider init={init}>
        <div className={styles.filtersWrapper}>
          <FiltersWidgetContent />
        </div>
      </ScopeDIProvider>
    </FiltersI18nProvider>
  );
});

declare module '@repo/di-ez' {
  interface ServicesCollectionMap {
    filtersService: FiltersService;
    filtersUiStore: FiltersUiStore;
  }
}
